import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { Input } from "components/form_components/Inputs";
import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import { getUserFacingMessage } from "lib/graphqlErrors";
import { useUpdateCustomerDocument } from "lib/hooks/api/customer_documents/useUpdateCustomerDocument";

type Props = {
  docId: string;
  fileName: string;
  isOpen: boolean;
  isFolder: boolean;
  onOpenChange: (isOpen: boolean) => void;
};
export function RenameCustomerDocumentModal({
  docId,
  fileName,
  isOpen,
  isFolder,
  onOpenChange,
}: Props) {
  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      // Only use the base name for the input
      fileName: fileName.replace(/\.[^/.]+$/, ""),
    },
    mode: "onSubmit",
  });

  const fileExtension = fileName.includes(".") ? fileName.substring(fileName.lastIndexOf(".")) : "";

  const onOpenModalChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      reset(); // Reset fileName when modal is closed
    }
  };

  const { mutate: updateCustomerDocument, isLoading } = useUpdateCustomerDocument({
    onSuccess: async (data) => {
      onOpenChange(false); // Close modal on success

      const { fileName: newFileName } = data.updateCustomerDocument[0];

      toast.success(`Renamed document ${fileName} to ${newFileName}`);
    },
    onError: (err, variables) => {
      const { fileName } = variables.request.updates[0];
      toast.error(getUserFacingMessage(err) ?? `Failed to rename document to ${fileName}`);
    },
  });

  return (
    <Modal open={isOpen} showCloseButton={false} onOpenChange={onOpenModalChange}>
      <Form
        aria-label="Rename modal"
        onSubmit={handleSubmit(({ fileName: newBaseName }) => {
          const newFileName = `${newBaseName}${fileExtension}`;
          updateCustomerDocument({
            request: {
              updates: [
                {
                  id: docId,
                  fileName: newFileName,
                  isFolder: isFolder,
                },
              ],
            },
          });
        })}
      >
        <Flex flexDirection="column" gap={4}>
          <Input
            width="100%"
            name="fileName"
            control={control}
            label={<Label>Rename</Label>}
            rules={{
              required: "File name is required",
              pattern: {
                value: /^[^<>:"/\\|?*]+$/, // Disallow invalid characters
                message:
                  'File name contains invalid characters (e.g. . / \\ : * ? " < > | or space)',
              },
            }}
            onBlur={(e) => {
              // Auto-trim on blur
              setValue("fileName", e.target.value.trim(), {
                shouldValidate: true,
              });
            }}
          />
        </Flex>
        <Flex gap={8} alignSelf="flex-end">
          <Button
            buttonType="secondary"
            disabled={isLoading}
            onClick={() => onOpenModalChange(false)}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            <Text color="white">Add</Text>
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
}

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  gap: 24,
  minWidth: "400px",
});

const Label = styled(Text)({
  fontWeight: 500,
  marginBottom: 4,
  fontSize: 16,
});
